import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetch, useStudentsByGroup } from '../../hooks';
import Filter from '../Filter';
import { TeacherFiltersEnum } from '../../constants';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppDispatch';
import { setSelectedGroup, setSelectedStudent } from '../../slices/filters/entity/FiltersSlice';
import { FiltersWrapper } from './Atoms';
import { IBookGroup } from 'src/config/api/models/Group';
import apiRoutesV2 from 'src/config/api/routes';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import TeacherFiltersControls from './TeacherFiltersControls';
import { LogActionTypes, useDataLogging } from '../../data-logging';

type Props = {
  visible?: boolean;
  type?: TeacherFiltersEnum;
  bookId?: string;
};

const TeacherFiltersOfType: FC<Props> = ({ visible = true, type, bookId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const log = useDataLogging();
  const filterMode = useAppSelector((state) => state.filters.filterMode);
  const selectedGroup = useAppSelector((state) => state.filters.selectedGroup);
  const selectedStudent = useAppSelector((state) => state.filters.selectedStudent);
  const isFetching = useAppSelector((state) => state.filters.isFetching);
  const trajectoryId = useAppSelector((state) => state.trajectories.trajectory?.id);

  const [groups, setGroups] = useState<IBookGroup[]>([]);
  const [bookGroupsResponse, getBookGroups] = useFetch<IBookGroup[]>(
    apiRoutesV2.bookGroups(bookId as string)
  );
  const { data: studentsData } = useStudentsByGroup(
    selectedGroup || '',
    Boolean(visible && selectedGroup)
  );

  const filtersVisible = useMemo(() => {
    return visible && !(type === TeacherFiltersEnum.FOLLOW_UP && filterMode === 'bookOverview');
  }, [visible, type, filterMode]);

  useEffect(() => {
    if (!visible) {
      setGroups([]);
      dispatch(setSelectedGroup(''));
    } else {
      getBookGroups();
    }
  }, [visible, setGroups, getBookGroups, dispatch]);

  useEffect(() => {
    if (bookGroupsResponse.success) {
      setGroups(bookGroupsResponse.data);
    }
  }, [bookGroupsResponse]);

  const handleChangeGroup = useCallback(
    (groupId: string) => {
      dispatch(setSelectedGroup(groupId));
      dispatch(setSelectedStudent(''));
      if (trajectoryId) {
        log(LogActionTypes.USER_GROUP_SCORES_BY_TRAJECTORY, {
          additionalData: { bookId, trajectoryId, groupId },
        });
      }
    },
    [bookId, dispatch, log, trajectoryId]
  );

  const handleChangeStudent = useCallback(
    (studentId: string) => {
      dispatch(setSelectedStudent(studentId));
      if (trajectoryId) {
        log(LogActionTypes.STUDENT_SCORES_BY_TRAJECTORY, {
          additionalData: { bookId, trajectoryId, groupId: selectedGroup, studentId },
        });
      }
    },
    [bookId, dispatch, log, selectedGroup, trajectoryId]
  );

  const groupFilterOptions = groups.map((group) => ({
    label: `${group.label} ${group.isDefault ? 'Klasgroep' : ''} (${group.amountOfStudents})`,
    id: group.id,
  }));

  const studentFilterOptions = studentsData
    ? studentsData.data.map((student) => ({
        id: student.id,
        label: student.fullName,
      }))
    : [];

  return filtersVisible && !!type && groups ? (
    <FiltersWrapper>
      <Filter
        id="group-filter"
        value={selectedGroup}
        onChange={handleChangeGroup}
        options={groupFilterOptions}
        icon={SpriteIconsLight.FOCUS_GROUP}
      />
      {filterMode !== 'scoring' && type !== TeacherFiltersEnum.VISIBILITY && (
        <Filter
          id="student-filter"
          value={selectedStudent}
          onChange={handleChangeStudent}
          options={studentFilterOptions}
          disabled={!selectedGroup || isFetching}
          placeholder={!!selectedGroup ? t('shared.forms.allStudents') : ''}
          emptyOption={!!selectedGroup}
        />
      )}
      <TeacherFiltersControls />
    </FiltersWrapper>
  ) : null;
};

export default TeacherFiltersOfType;
